import React, { useState, useEffect } from 'react';
import { X, History, ArrowRight, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { getStatusLogs } from '../../services/api';

const STATUS_COLORS = {
  pending: { bg: 'bg-red-100', text: 'text-red-600' },
  informed: { bg: 'bg-blue-100', text: 'text-blue-600' },
  completed: { bg: 'bg-green-100', text: 'text-green-600' },
  cancelled: { bg: 'bg-gray-100', text: 'text-gray-600' },
};

function getWeekRange(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const mon = new Date(now);
  mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return { start: mon, end: sun };
}

function formatWeekLabel(offset) {
  if (offset === 0) return 'Bu Hafta';
  if (offset === -1) return 'Geçen Hafta';
  const { start, end } = getWeekRange(offset);
  const fmt = (d) => d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  return `${fmt(start)} - ${fmt(end)}`;
}

export default function StatusHistoryModal({ onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const res = await getStatusLogs(200);
        setLogs(res.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const { start: weekStart, end: weekEnd } = getWeekRange(weekOffset);

  const weekFiltered = logs.filter(l => {
    const d = new Date(l.timestamp);
    return d >= weekStart && d <= weekEnd;
  });

  const filtered = search.trim()
    ? weekFiltered.filter(l =>
        l.user_name?.toLowerCase().includes(search.toLowerCase()) ||
        l.customer_name?.toLowerCase().includes(search.toLowerCase())
      )
    : weekFiltered;

  const fmtTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  const Badge = ({ status, label }) => {
    const c = STATUS_COLORS[status] || STATUS_COLORS.pending;
    return (
      <span className={`inline-flex items-center px-1.5 py-0 rounded text-[9px] font-bold ${c.bg} ${c.text}`}>
        {label}
      </span>
    );
  };

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-50 flex sm:inset-0 sm:items-center sm:justify-center sm:p-4" data-testid="status-history-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white sm:rounded-2xl shadow-2xl w-full sm:max-w-md lg:max-w-3xl h-full sm:h-auto sm:max-h-[75vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <History size={16} className="text-[#6b6de3]" />
            <h3 className="text-sm font-bold text-gray-900">Durum Değişiklik Geçmişi</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100" data-testid="history-modal-close">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-2 border-b border-gray-50">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Ekip üyesi veya müşteri ara..."
              className="w-full pl-8 pr-3 py-1.5 text-xs bg-[#F2F2F7] border-0 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#6b6de3]"
              data-testid="history-search-input"
            />
          </div>
        </div>

        {/* Week Selector */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-gray-50 bg-[#F2F2F7]/50">
          <button onClick={() => setWeekOffset(w => w - 1)} className="p-1.5 rounded-lg hover:bg-white text-[#8E8E93]" data-testid="week-prev">
            <ChevronLeft size={16} />
          </button>
          <button onClick={() => setWeekOffset(0)} className={`text-xs font-semibold px-3 py-1 rounded-lg transition-colors ${weekOffset === 0 ? 'text-[#6b6de3] bg-[#6b6de3]/10' : 'text-gray-700 hover:bg-white'}`} data-testid="week-label">
            {formatWeekLabel(weekOffset)}
          </button>
          <button onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0} className="p-1.5 rounded-lg hover:bg-white text-[#8E8E93] disabled:opacity-30" data-testid="week-next">
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-5 h-5 border-2 border-[#6b6de3] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <History size={24} className="mx-auto mb-2 text-gray-300" />
              <p className="text-xs text-[#8E8E93]">{search ? 'Sonuç bulunamadı' : 'Henüz durum değişikliği yok'}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {filtered.map((log) => (
                <div key={log.id} className="px-4 py-2 hover:bg-gray-50/50 transition-colors" data-testid={`log-${log.id}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-bold text-[#6b6de3]">{log.user_name}</span>
                    <span className="text-[9px] text-[#8E8E93]">{fmtTime(log.timestamp)}</span>
                    <span className="ml-auto flex items-center gap-1">
                      <Badge status={log.old_status} label={log.old_label} />
                      <ArrowRight size={10} className="text-gray-400" />
                      <Badge status={log.new_status} label={log.new_label} />
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-gray-500 mt-0.5">
                    {log.customer_name && <span>{log.customer_name}</span>}
                    {log.pet_name && <><span className="text-gray-300">·</span><span>{log.pet_name}</span></>}
                    {log.operation_name && <><span className="text-gray-300">·</span><span className="font-medium text-[#6b6de3]">{log.operation_name}</span></>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
